import { applyDesignerCommand, type DesignLayer, type DesignerCommand } from '@pz/scene';
import type { DesignerCtx, ToolId } from './panels.js';

/**
 * The one way the designer changes a design.
 *
 * Every panel, toolbar button, context-menu item and keyboard shortcut ends
 * here: `applyDesignerCommand` validates and applies, the store records one
 * undo step, and a refusal becomes a sentence in a toast instead of a silent
 * no-op. Before the rewrite the same edit had three paths (a raw `update`
 * patch, a `properties` command, a bespoke reducer action) and they disagreed
 * about opacity, flips and paint order; the rule now is that anything a
 * shopper can undo goes through `run`.
 */
export function run(ctx: DesignerCtx, command: DesignerCommand, tag?: string, options: { quiet?: boolean } = {}): boolean {
  const refusal = refuse(ctx, command);
  if (refusal) {
    if (!options.quiet) ctx.toast(refusal, 'error');
    return false;
  }
  try {
    const previous = ctx.store.value();
    const answer = applyDesignerCommand(ctx.rawConfig, previous, command);
    const added = answer.layers.find((layer) => !previous.layers.some((old) => old.id === layer.id));
    if (added) {
      ctx.selection.clear();
      ctx.selection.add(added.id);
    }
    ctx.dispatch({ type: 'set', answer, select: added?.id ?? ctx.selected()?.id ?? null, ...(tag ? { tag } : {}) });
    return true;
  } catch (error) {
    // `quiet`: a best-effort step (the quick start's closing arrangement)
    // whose failure leaves a perfectly good design behind.
    if (!options.quiet) ctx.toast(error instanceof Error ? error.message : 'Izmena nije moguća', 'error');
    return false;
  }
}

/**
 * Zazzle's "Selling Options" in force, on our side of the wire.
 *
 * `permanent` means a shopper may move and restyle the element but never lose
 * it; `interactive: false` means they may not touch it at all. The platform
 * refuses the same two things (P owns that), but a refusal that only arrives
 * from the command layer arrives as a thrown sentence in English — and a
 * delete button that looks live until it is pressed is worse than one that
 * explains itself. So the designer checks first, in Serbian.
 */
function refuse(ctx: DesignerCtx, command: DesignerCommand): string | undefined {
  const ids = idsOf(command);
  if (!ids.length) return undefined;
  const layers = ctx.store.value().layers.filter((layer) => ids.includes(layer.id));
  const authoring = (command as { authoring?: boolean }).authoring === true;
  if (authoring) return undefined;
  if (command.op === 'delete' && layers.some((layer) => layer.permanent)) {
    return ctx.texts.permanentRefused;
  }
  if (layers.some((layer) => layer.interactive === false)) {
    return ctx.texts.notInteractive;
  }
  return undefined;
}

function idsOf(command: DesignerCommand): string[] {
  const ids = (command as { ids?: unknown }).ids;
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [];
}

/** True when the shopper is allowed to remove this layer at all. */
export function canDelete(layer: DesignLayer | null | undefined): boolean {
  return Boolean(layer) && !layer!.permanent && layer!.interactive !== false;
}

/** True when the shopper may select, move or restyle it. */
export function canEdit(layer: DesignLayer | null | undefined): boolean {
  return Boolean(layer) && layer!.interactive !== false;
}

/** Which rail panel owns a layer — the double-click and "Ispravi" target. */
export function toolFor(layer: DesignLayer): ToolId {
  switch (layer.kind) {
    case 'text': return 'text';
    case 'image': return 'image';
    case 'shape': return 'elements';
    case 'qr': return 'elements';
    default: return 'artwork';
  }
}

/**
 * What a layer is called in the layer list, the Šabloni quick fields and the
 * Review checklist. A template object's authored label wins over everything:
 * it is the word the shop chose for the field ("Vaše ime"), and a shopper who
 * reads it on the product card must find the same word here.
 */
export function layerLabel(layer: DesignLayer): string | undefined {
  return layer.template?.label || layer.name || undefined;
}

/** True when a template object still carries the content the author put there. */
export function templateUnreplaced(layer: DesignLayer): boolean {
  const template = layer.template;
  if (!template?.mustReplace) return false;
  const original = template.original;
  if (original === undefined) return false;
  if (layer.kind === 'text') return layer.text.trim() === original.trim();
  if (layer.kind === 'image' || layer.kind === 'artwork') return layer.assetId === original;
  return false;
}
